import React, { ReactNode } from 'react';
import Spinner from '../Spinner';

export type ButtonType = 'primary' | 'secondary' | 'custom';
export type ComponentSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl';
export interface BaseButtonProps {
  color?: ButtonType;
  /**
   * If added, the button will show an icon before the button's label.
   * @type React.ReactElement
   */
  leftIcon?: React.ReactElement;
  /**
   * If added, the button will show an icon after the button's label.
   * @type React.ReactElement
   */
  rightIcon?: React.ReactElement;

  size?: ComponentSize;
  className?: string;
  children?: ReactNode;
  /**
   * If `true`, the button will be disabled.
   */
  disabled?: boolean;
  /**
   * If `true`, the button will show a spinner.
   */
  loading?: boolean;
}

function classNames(...classes: string[]) {
  return classes.filter(Boolean).join(' ');
}

type HTMLButtonProps = React.ButtonHTMLAttributes<HTMLButtonElement>;

export type ButtonProps = BaseButtonProps & HTMLButtonProps;

const BASE_BUTTON = 'inline-flex items-center border border-transparent ';
const PRIMARY =
  'text-white group-[.defaultSidebar]:bg-white group-[.defaultSidebar]:hover:bg-primary-50 group-[.defaultSidebar]:text-primary-900 bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500';
const SECONDARY =
  'text-white bg-secondary-600 hover:bg-secondary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-secondary-500';

// button size varition
const XS = `${BASE_BUTTON} px-2.5 py-1.5 text-xs font-medium rounded shadow-sm`;
const SM = `${BASE_BUTTON} px-3 py-2 text-sm leading-4 font-medium rounded-md shadow-sm`;
const MD = `${BASE_BUTTON} px-4 py-2 text-sm font-medium rounded-md shadow-sm`;
const LG = `${BASE_BUTTON} px-4 py-2  text-base font-medium rounded-md shadow-sm`;
const XL = `${BASE_BUTTON} px-6 py-3 text-base font-medium rounded-md shadow-sm`;

const Button: React.FC<ButtonProps> = (props) => {
  const {
    color = 'primary',
    leftIcon,
    rightIcon,
    size = 'md',
    className,
    children,
    disabled = false,
    loading,
    ...rest
  } = props;

  const childrenWithIcon =
    !leftIcon && !rightIcon ? (
      children
    ) : leftIcon ? (
      <>
        {leftIcon}
        {children}
      </>
    ) : rightIcon ? (
      <>
        {children}
        {rightIcon}
      </>
    ) : null;

  if (!disabled) {
    return (
      <button
        className={
          color !== 'custom'
            ? classNames(
                size === 'xl'
                  ? `${XL}`
                  : size === 'lg'
                  ? `${LG}`
                  : size === 'sm'
                  ? `${SM}`
                  : size === 'xs'
                  ? `${XS}`
                  : `${MD}`,

                color === 'primary' ? `${PRIMARY}` : `${SECONDARY}`,

                `${BASE_BUTTON} ${className}`
              )
            : ' ' + className
        }
        {...rest}
      >
        {childrenWithIcon}
        {loading && <Spinner />}
      </button>
    );
  }

  if (disabled) {
    return (
      <button
        className={classNames(
          size === 'xl'
            ? `${XL}`
            : size === 'lg'
            ? `${LG}`
            : size === 'sm'
            ? `${SM}`
            : size === 'xs'
            ? `${XS}`
            : `${MD}`,

          color === 'primary' ? `${PRIMARY}` : `${SECONDARY}`,

          `${BASE_BUTTON} disabled:opacity-50 cursor-not-allowed ${className}`
        )}
        disabled
        {...rest}
      >
        {childrenWithIcon}
        {loading && <Spinner />}
      </button>
    );
  }

  return (
    <button
      className={classNames(
        size === 'xl'
          ? `${XL}`
          : size === 'lg'
          ? `${LG}`
          : size === 'sm'
          ? `${SM}`
          : size === 'xs'
          ? `${XS}`
          : `${MD}`,
        `${BASE_BUTTON} ${className}`
      )}
      {...rest}
    >
      {childrenWithIcon}
      {loading && <Spinner />}
    </button>
  );
};

export default Button;
